import { apiClient } from '@/api/client';
import { MenuItem } from '@/types/menu-item';
import { mapMenuItemWireToDomain } from '../mappers/menu-item';
import { menuItemWireSchema, paginatedMenuItemResponseSchema } from '../schemas/menu-item';

/**
 * Domain-oriented API module for menu item operations
 * Provides typed functions for fetching menu items from the Gateway API
 */

/**
 * Query parameters for listing menu items
 */
export interface ListMenuItemsParams {
  business_id?: string;
  page?: number;
  per_page?: number;
  sort_by?: 'name' | 'created_at' | 'updated_at';
  sort_order?: 'asc' | 'desc';
}

/**
 * Query parameters for searching menu items
 */
export interface SearchMenuItemsParams {
  query: string;
  business_id?: string;
  page?: number;
  per_page?: number;
}

/**
 * Menu item wire format (matches Gateway API response)
 * Based on MenuItemReadSchema from Gateway
 */
export interface MenuItemWire {
  id: string;
  business_id: string;
  name: string;
  description?: string | null;
  cover_photo_r2_key?: string | null;
  status: 'approved' | 'pending';
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  business_name?: string;
  check_in_count?: number;
}

/**
 * Paginated response wrapper
 * Matches createPaginatedResponseSchema from Gateway
 */
export interface PaginatedMenuItemResponse {
  items: MenuItem[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

/**
 * Build query string from parameters
 */
function buildQueryString(params: ListMenuItemsParams): string {
  const queryParams: string[] = [];

  if (params.business_id) {
    queryParams.push(`business_id=${encodeURIComponent(params.business_id)}`);
  }
  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }
  if (params.sort_by) {
    queryParams.push(`sort_by=${params.sort_by}`);
  }
  if (params.sort_order) {
    queryParams.push(`sort_order=${params.sort_order}`);
  }

  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
}

/**
 * Build query string from search parameters
 */
function buildSearchQueryString(params: SearchMenuItemsParams): string {
  const queryParams: string[] = [];

  queryParams.push(`query=${encodeURIComponent(params.query)}`);
  if (params.business_id) {
    queryParams.push(`business_id=${encodeURIComponent(params.business_id)}`);
  }
  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }

  return `?${queryParams.join('&')}`;
}

/**
 * List menu items with optional filtering and pagination
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated list of menu items
 *
 * @example
 * ```typescript
 * // List all menu items
 * const result = await listMenuItems();
 *
 * // Filter by business
 * const result = await listMenuItems({ business_id: 'business-123' });
 *
 * // With pagination
 * const result = await listMenuItems({ page: 2, per_page: 10 });
 * ```
 */
export async function listMenuItems(
  params: ListMenuItemsParams = {}
): Promise<PaginatedMenuItemResponse> {
  const queryParams: ListMenuItemsParams = {
    page: 1,
    per_page: 20,
    sort_by: 'name',
    sort_order: 'asc',
    ...params,
  };

  const queryString = buildQueryString(queryParams);
  const endpoint = `/api/menu-items${queryString}`;

  const wireData = await apiClient.get<{
    items: MenuItemWire[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  }>(endpoint);

  // Validate response with zod schema
  const validatedData = paginatedMenuItemResponseSchema.parse(wireData);

  // Map wire format to domain format
  return {
    items: validatedData.items.map(mapMenuItemWireToDomain),
    total: validatedData.total,
    page: validatedData.page,
    per_page: validatedData.per_page,
    total_pages: validatedData.total_pages,
  };
}

/**
 * Search menu items with text query and optional filters
 *
 * @param params - Search parameters including query text, business filter, and pagination
 * @returns Paginated list of menu items matching the search
 *
 * @example
 * ```typescript
 * // Search for "pizza" across all businesses
 * const result = await searchMenuItems({
 *   query: 'pizza',
 *   per_page: 10
 * });
 *
 * // Search within a specific business
 * const result = await searchMenuItems({
 *   query: 'burger',
 *   business_id: 'business-123'
 * });
 * ```
 */
export async function searchMenuItems(
  params: SearchMenuItemsParams
): Promise<PaginatedMenuItemResponse> {
  const queryParams: SearchMenuItemsParams = {
    page: 1,
    per_page: 20,
    ...params,
  };

  const queryString = buildSearchQueryString(queryParams);
  const endpoint = `/api/menu-items/search${queryString}`;

  const wireData = await apiClient.get<{
    items: MenuItemWire[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  }>(endpoint);

  // Validate response with zod schema
  const validatedData = paginatedMenuItemResponseSchema.parse(wireData);

  // Map wire format to domain format
  return {
    items: validatedData.items.map(mapMenuItemWireToDomain),
    total: validatedData.total,
    page: validatedData.page,
    per_page: validatedData.per_page,
    total_pages: validatedData.total_pages,
  };
}

/**
 * Get a single menu item by ID
 *
 * @param id - Menu item ID
 * @returns Menu item domain model
 *
 * @example
 * ```typescript
 * const menuItem = await getMenuItemById('menu-item-123');
 * ```
 */
export async function getMenuItemById(id: string): Promise<MenuItem> {
  const endpoint = `/api/menu-items/${id}`;
  const wireData = await apiClient.get<MenuItemWire>(endpoint);

  // Validate response with zod schema
  const validatedData = menuItemWireSchema.parse(wireData);

  // Map wire format to domain format
  return mapMenuItemWireToDomain(validatedData);
}

/**
 * Request body for suggesting a new menu item
 */
export interface SuggestMenuItemRequest {
  business_id: string;
  name: string;
  description?: string;
}

/**
 * Suggest a new menu item for a business
 *
 * Creates a menu item with pending status that needs admin approval.
 *
 * @param data - Suggestion data including business_id, name, and optional description
 * @returns Created menu item with pending status
 */
export async function suggestMenuItem(data: SuggestMenuItemRequest): Promise<MenuItem> {
  const wireData = await apiClient.post<MenuItemWire>('/api/menu-items/suggest', data);

  // Validate response with zod schema
  const validatedData = menuItemWireSchema.parse(wireData);

  // Map wire format to domain format
  return mapMenuItemWireToDomain(validatedData);
}
